/**
 * Container registry credentials management.
 *
 * Sibling of the Providers tab — Providers covers cloud / model / object
 * storage credentials (one row per SkyPilot ``kind``), Container
 * Registries covers ``docker login`` credentials (one row per
 * ``registry`` host). Both projects to ``~/.docker/config.json`` on the
 * controller and into the SkyPilot rendered task envs so private
 * workload images pull on every SkyPilot replica.
 */
import { useMemo, useState } from "react";
import { Boxes, Container, KeyRound, Pencil, Trash2 } from "lucide-react";

import {
  REGISTRY_SUGGESTIONS,
  useContainerRegistries,
  useCreateContainerRegistry,
  useDeleteContainerRegistry,
  useUpdateContainerRegistry,
} from "@/api/local/container_registries";
import type {
  ContainerRegistryAuthOut,
  ContainerRegistryAuthUpsertBody,
} from "@/api/types";
import { useConfirm } from "@/components/ConfirmDialog";
import { Pill } from "@/components/Pill";
import { Section } from "@/components/Section";

interface EditorState {
  mode: "create" | "edit";
  /** Only set when ``mode === "edit"``. */
  source?: ContainerRegistryAuthOut;
  draft: ContainerRegistryAuthUpsertBody;
  /** Operator-friendly error message from the last save attempt. */
  error?: string;
}

const EMPTY_DRAFT: ContainerRegistryAuthUpsertBody = {
  name: "",
  registry: "ghcr.io",
  username: "",
  password: "",
  service_id: null,
  pool_id: null,
  enabled: true,
  note: null,
};

export function GlobalContainerRegistries() {
  const list = useContainerRegistries();
  const createRegistry = useCreateContainerRegistry();
  const updateRegistry = useUpdateContainerRegistry();
  const deleteRegistry = useDeleteContainerRegistry();
  const confirm = useConfirm();
  const [editor, setEditor] = useState<EditorState | null>(null);

  // Only global rows are shown here; service- / pool-scoped overrides
  // belong on the respective scope's Providers-style tab (a future
  // extension — for now operators reach them through the API directly).
  const globalRows = useMemo(
    () => (list.data ?? []).filter((r) => r.scope === "global"),
    [list.data],
  );

  const startCreate = () => {
    setEditor({ mode: "create", draft: { ...EMPTY_DRAFT } });
  };

  const startEdit = (row: ContainerRegistryAuthOut) => {
    setEditor({
      mode: "edit",
      source: row,
      // Password stays empty in the editor: an empty value on PUT
      // tells the backend "keep the stored value untouched" so the
      // operator doesn't have to retype the PAT on every save.
      draft: {
        name: row.name,
        registry: row.registry,
        username: row.username,
        password: "",
        service_id: row.service_id,
        pool_id: row.pool_id,
        enabled: row.enabled,
        note: row.note,
      },
    });
  };

  const handleSave = async () => {
    if (!editor) return;
    try {
      if (editor.mode === "create") {
        await createRegistry(editor.draft);
      } else if (editor.source) {
        await updateRegistry(editor.source.id, editor.draft);
      }
      setEditor(null);
    } catch (err) {
      setEditor({
        ...editor,
        error: err instanceof Error ? err.message : "save failed",
      });
    }
  };

  return (
    <div className="stack" style={{ display: "grid", gap: 16 }}>
      <Section
        title="Container registries"
        icon={<Container />}
        count={`${globalRows.length} ${globalRows.length === 1 ? "row" : "rows"}`}
        flush
      >
        <p
          className="muted"
          style={{ padding: "10px 18px 0", margin: 0, fontSize: 12.5 }}
        >
          Docker credentials projected to <code>~/.docker/config.json</code>
          on the controller and injected as
          <code> SKYPILOT_DOCKER_* </code>envs on every SkyPilot replica
          that pulls from a matching registry. Used by image probing on
          the workload form and by the rendered service.yaml setup
          block; the password value itself never leaves the controller.
        </p>
        <div className="table-wrap">
          <table className="data">
            <thead>
              <tr>
                <th>Name</th>
                <th>Registry</th>
                <th>Username</th>
                <th>Credential</th>
                <th style={{ width: 90 }}>Enabled</th>
                <th>Note</th>
                <th style={{ width: 1 }} />
              </tr>
            </thead>
            <tbody>
              {globalRows.map((row) => (
                <tr key={row.id} className="hoverable">
                  <td>
                    <strong>{row.name}</strong>
                  </td>
                  <td className="mono">{row.registry}</td>
                  <td className="mono">{row.username}</td>
                  <td>
                    {row.password_last4 ? (
                      <Pill tone="ok">…{row.password_last4}</Pill>
                    ) : (
                      <Pill tone="warn">not set</Pill>
                    )}
                    {row.rotated_at && (
                      <div
                        className="muted"
                        style={{ fontSize: 11, marginTop: 4 }}
                      >
                        rotated{" "}
                        {new Date(row.rotated_at).toLocaleString()}
                      </div>
                    )}
                  </td>
                  <td>
                    <Pill tone={row.enabled ? "ok" : "neutral"}>
                      {row.enabled ? "on" : "off"}
                    </Pill>
                  </td>
                  <td className="muted">{row.note ?? "—"}</td>
                  <td className="actions">
                    <div className="toolbar" style={{ gap: 6 }}>
                      <button
                        type="button"
                        className="btn btn--sm"
                        onClick={() => startEdit(row)}
                      >
                        <Pencil />
                        edit
                      </button>
                      <button
                        type="button"
                        className="btn btn--sm btn--danger"
                        onClick={async () => {
                          const ok = await confirm({
                            title: "Delete registry credential?",
                            tone: "danger",
                            confirmLabel: "Delete",
                            description: (
                              <p style={{ marginTop: 0 }}>
                                Delete registry credential{" "}
                                <strong>{row.name}</strong>{" "}
                                <span className="muted">
                                  (<code className="mono">{row.registry}</code>)
                                </span>
                                ?
                              </p>
                            ),
                          });
                          if (ok) void deleteRegistry(row.id);
                        }}
                      >
                        <Trash2 />
                        delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {!globalRows.length && (
                <tr>
                  <td colSpan={7} className="empty">
                    <Boxes
                      style={{
                        width: 16,
                        height: 16,
                        verticalAlign: "middle",
                        marginRight: 6,
                      }}
                    />
                    no registry credentials registered yet — add one to
                    pull from a private GHCR / Docker Hub / custom
                    registry
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        <div className="toolbar" style={{ padding: 12 }}>
          <button
            type="button"
            className="btn btn--primary btn--sm"
            onClick={startCreate}
          >
            <KeyRound />
            add registry credential
          </button>
        </div>
      </Section>

      {editor && (
        <RegistryEditor
          editor={editor}
          onChange={(patch) =>
            setEditor({
              ...editor,
              draft: { ...editor.draft, ...patch },
              error: undefined,
            })
          }
          onCancel={() => setEditor(null)}
          onSave={handleSave}
        />
      )}
    </div>
  );
}

interface RegistryEditorProps {
  editor: EditorState;
  onChange: (patch: Partial<ContainerRegistryAuthUpsertBody>) => void;
  onCancel: () => void;
  onSave: () => void;
}

function RegistryEditor({
  editor,
  onChange,
  onCancel,
  onSave,
}: RegistryEditorProps) {
  const isEdit = editor.mode === "edit";
  return (
    <Section
      title={isEdit ? "Edit registry credential" : "New registry credential"}
      icon={<KeyRound />}
      flush
    >
      <div
        style={{
          display: "grid",
          gap: 12,
          padding: 16,
          gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
        }}
      >
        <label className="stack" style={{ display: "grid", gap: 4 }}>
          <span className="muted" style={{ fontSize: 12 }}>
            Name
          </span>
          <input
            className="input"
            type="text"
            value={editor.draft.name}
            placeholder="ghcr-spiral-ai"
            onChange={(e) => onChange({ name: e.target.value })}
          />
        </label>

        <label className="stack" style={{ display: "grid", gap: 4 }}>
          <span className="muted" style={{ fontSize: 12 }}>
            Registry host
          </span>
          <input
            className="input"
            type="text"
            list="registry-suggestions"
            value={editor.draft.registry}
            placeholder="ghcr.io"
            onChange={(e) => onChange({ registry: e.target.value })}
          />
          <datalist id="registry-suggestions">
            {REGISTRY_SUGGESTIONS.map((host) => (
              <option key={host} value={host} />
            ))}
          </datalist>
        </label>

        <label className="stack" style={{ display: "grid", gap: 4 }}>
          <span className="muted" style={{ fontSize: 12 }}>
            Username
          </span>
          <input
            className="input"
            type="text"
            value={editor.draft.username}
            placeholder="ghcr-user-or-org"
            onChange={(e) => onChange({ username: e.target.value })}
          />
        </label>

        <label className="stack" style={{ display: "grid", gap: 4 }}>
          <span className="muted" style={{ fontSize: 12 }}>
            Password / PAT
            {isEdit && (
              <em
                className="muted"
                style={{ marginLeft: 6, fontStyle: "normal" }}
              >
                (leave blank to keep current)
              </em>
            )}
          </span>
          <input
            className="input"
            type="password"
            value={editor.draft.password ?? ""}
            placeholder={
              isEdit ? "…leave empty to keep current" : "github_pat_…"
            }
            autoComplete="new-password"
            onChange={(e) => onChange({ password: e.target.value })}
          />
        </label>

        <label
          className="stack"
          style={{ display: "grid", gap: 4, gridColumn: "1 / -1" }}
        >
          <span className="muted" style={{ fontSize: 12 }}>
            Note
          </span>
          <input
            className="input"
            type="text"
            value={editor.draft.note ?? ""}
            placeholder="optional"
            onChange={(e) =>
              onChange({ note: e.target.value || null })
            }
          />
        </label>

        <label
          className="toolbar"
          style={{ gap: 8, gridColumn: "1 / -1" }}
        >
          <input
            type="checkbox"
            checked={editor.draft.enabled ?? true}
            onChange={(e) => onChange({ enabled: e.target.checked })}
          />
          <span>Enabled</span>
        </label>

        {editor.error && (
          <div
            className="callout callout--danger"
            style={{ gridColumn: "1 / -1" }}
          >
            {editor.error}
          </div>
        )}

        <div
          className="toolbar"
          style={{ gap: 8, gridColumn: "1 / -1", justifyContent: "flex-end" }}
        >
          <button type="button" className="btn btn--sm" onClick={onCancel}>
            cancel
          </button>
          <button
            type="button"
            className="btn btn--primary btn--sm"
            onClick={onSave}
          >
            {isEdit ? "save changes" : "create"}
          </button>
        </div>
      </div>
    </Section>
  );
}
